
import { User } from '../types';
import { APP_CONFIG } from '../constants';
import { functions } from '../../infrastructure/firebaseConfig';
import { httpsCallable } from "firebase/functions";
import { logger } from '../../infrastructure/logger';

interface GuestInfo {
    name: string;
    email: string;
    dni: string;
    phone: string;
}

interface CheckoutRequest {
    items: { productId: string; quantity: number }[];
    user: User | null;
    guestInfo: GuestInfo | null;
    paymentMethod: string;
    walletUsed: number;
    shippingMethod: 'local' | 'national' | 'pickup' | null;
    zipCode: string;
    couponCode: string | null;
    existingOrderId?: string; // Add this
}

interface PreferenceRequest {
    orderId: string;
    title: string;
    price: number;
    email: string;
    items?: Record<string, unknown>[]; // Better than any[]
}

interface CheckoutResult {
    success: boolean;
    orderId?: string;
    error?: string;
}

interface CloudOrderResponse {
    success: boolean;
    orderId?: string;
    totalToPay?: number;
    error?: string;
}

interface CloudPreferenceResponse {
    init_point?: string;
    sandbox_init_point?: string;
}

export const checkoutService = {
    /**
     * SECURE SERVER-SIDE ORDER PROCESSING + MERCADO PAGO INTEGRATION
     */
    async processOrder(req: CheckoutRequest): Promise<CheckoutResult> {
        try {
            // 1. Process Order in Backend (Deducts stock, creates doc)
            const processOrderFn = httpsCallable<CheckoutRequest, CloudOrderResponse>(functions, 'processOrder');
            const orderResponse = await processOrderFn({
                items: req.items,
                user: req.user,
                paymentMethod: req.paymentMethod,
                walletUsed: req.walletUsed,
                shippingMethod: req.shippingMethod,
                zipCode: req.zipCode,
                guestInfo: req.guestInfo,
                couponCode: req.couponCode,
                existingOrderId: req.existingOrderId
            });

            const orderResult = orderResponse.data;

            if (!orderResult.success) {
                throw new Error(orderResult.error || "Error al procesar la orden");
            }

            const orderId = orderResult.orderId;

            // 2. Handle Mercado Pago Redirection if selected
            if (req.paymentMethod === 'mercadopago' && orderId && orderResult.totalToPay) {
                const createPrefFn = httpsCallable<PreferenceRequest, CloudPreferenceResponse>(functions, 'createPaymentPreference');
                
                // We need to pass the actual items with prices, which are in the orderResult if we returned them, 
                // but processOrder currently returns minimal info. 
                // However, we have the original items in req.items, but need prices.
                // Better approach: Let processOrder return the processed items or trust the total.
                // For now, we will pass the total as a single item fallback logic in cloud function handled this,
                // BUT we updated cloud function to expect items.
                // We should pass the 'finalItems' that processOrder generated? 
                // Since we can't see them here easily without refetching, 
                // let's pass a generic item with the total for stability, 
                // OR better, update processOrder to return the items.
                
                // Ideally processOrder returns the items. Let's assume we send what we have 
                // and the backend validates or constructs the preference.
                // The backend fix I just made handles "items" if present, or falls back to total.
                // Let's send the total as the primary source of truth for now to fix the crash.
                
                const prefResponse = await createPrefFn({
                    orderId: orderId,
                    title: `Pedido AllTuc #${orderId}`,
                    price: orderResult.totalToPay, 
                    email: req.user?.email || req.guestInfo?.email || APP_CONFIG.CONTACT.paymentsEmail,
                    items: [] // Send empty to trigger the fallback logic in backend which uses total
                });

                const prefResult = prefResponse.data;
                
                if (prefResult.init_point) {
                    window.location.href = prefResult.init_point;
                    return { success: true, orderId }; 
                } else {
                    throw new Error("No se pudo generar el link de Mercado Pago.");
                }
            }

            // 3. For other methods (Cash/Wallet), just return success
            return { success: true, orderId };

        } catch (err) {
            logger.error("Cloud Checkout Error", err instanceof Error ? err : new Error(String(err)));
            const error = err as { code?: string; message: string };
            
            let message = "Error interno al procesar la orden.";
            if (error.code === 'failed-precondition') message = error.message; 
            if (error.code === 'unauthenticated') message = "Tu sesión ha expirado.";
            if (error.code === 'not-found') message = "Uno de los productos ya no existe.";

            return { success: false, error: message };
        }
    }
};
